"use client";

import { useEffect, useRef, useState } from "react";
import { site } from "@/lib/site";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

/**
 * Siteye uyumlu, göze batmayan reklam paneli.
 * - Küçük "Reklam" etiketi (yasal + dürüst)
 * - Sabit/rezerve alan → CLS yok
 * - Reklam dolmazsa panel kendini gizler (boş kutu kalmaz)
 * - Geliştirmede (localhost) tasarımı göstermek için zarif önizleme;
 *   canlıda gerçek AdSense birimi.
 */
export function Reklam({
  slot,
  className,
  yan = false,
}: {
  slot?: string;
  className?: string;
  /** Kenar (sidebar) reklamı — dar ve dikey, sayfayla birlikte kayar */
  yan?: boolean;
}) {
  const onizleme = process.env.NODE_ENV !== "production";
  const insRef = useRef<HTMLModElement>(null);
  const [gizle, setGizle] = useState(false);

  useEffect(() => {
    if (onizleme || !slot || !site.adsensePubId) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* yoksay */
    }
    // Reklam dolmazsa paneli gizle (boş kutu görünmesin)
    const t = setTimeout(() => {
      if (insRef.current?.getAttribute("data-ad-status") === "unfilled") setGizle(true);
    }, 2000);
    return () => clearTimeout(t);
  }, [onizleme, slot]);

  // Canlıda slot yoksa hiç render etme
  if (!onizleme && (!slot || !site.adsensePubId)) return null;
  if (gizle) return null;

  return (
    <aside
      className={cn(yan ? "w-full" : "mx-auto w-full max-w-3xl", className)}
      aria-label="Reklam"
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-2xl border border-border bg-surface-muted/40",
          yan && "sticky top-24",
        )}
      >
        <span className="absolute right-3 top-2 z-10 text-[10px] font-medium uppercase tracking-wider text-muted/70">
          Reklam
        </span>
        {onizleme ? (
          <div
            className={cn(
              "flex items-center justify-center px-6 text-center",
              yan ? "min-h-[420px] py-8" : "min-h-[120px] py-8",
            )}
          >
            <div>
              <div className="mx-auto mb-2 size-2 rounded-full bg-brand/60" />
              <p className="text-sm font-medium text-muted">Reklam alanı</p>
              <p className="mt-0.5 text-xs text-muted/70">
                {yan ? "(kenar)" : "(içerik arası)"}
              </p>
            </div>
          </div>
        ) : (
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={yan ? { display: "block", minHeight: 420 } : { display: "block" }}
            data-ad-client={site.adsensePubId}
            data-ad-slot={slot}
            data-ad-format={yan ? "vertical" : "auto"}
            data-full-width-responsive={yan ? "false" : "true"}
          />
        )}
      </div>
    </aside>
  );
}
